/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';
import { AdminContext } from '@/contexts/admin-context';
import { TelemetryContext } from '@/contexts/telemetry-context';
import PermissionGate from '@/components/permission-gate';
import statuses from './statuses';
import sendEmail from './send-email';

export default function SendButton({
  id,
  requestData,
  labelText,
  templateId,
  buttonText,
  showLabel,
  fontSize,
  showBorder,
  telemetryEvent,
  telemetryValue,
  permissionId,
}) {
  const { translate } = useContext(LocaleContext);
  const telemetryContext = useContext(TelemetryContext);
  const { getCarrierName } = useContext(AdminContext);
  const [status, setStatus] = useState(statuses.NONE);

  const isDisabled = status !== undefined && status !== statuses.NONE;

  const calculateButtonColor = () => {
    if (status === statuses.SENT) return 'var(--color-green)';
    if (status === statuses.FAILED) return 'var(--color-error-red)';
    return 'var(--color-white)';
  };

  const calculateButtonTextColor = () => {
    if (status === statuses.SENT) return 'var(--color-white)';
    if (status === statuses.FAILED) return 'var(--color-white)';
    return 'var(--color-primary-dark)';
  };

  const calculateButtonText = () => {
    if (status === statuses.SENDING) return 'sending';
    if (status === statuses.SENT) return 'sent';
    if (status === statuses.FAILED) return 'failed';
    return buttonText;
  };

  const sendEmailWithData = async () => {
    setStatus(statuses.SENDING);
    sendEmail({
      templateId,
      orgId: getCarrierName(),
      requestData,
      setStatus,
    });
    if (telemetryEvent) {
      telemetryContext?.sendEvent(telemetryEvent, {
        ...telemetryValue && ({ email_previewed: telemetryValue }),
      });
    }
  };

  return (
    <div key={id} id={id}>
      <div className="control">
        {showLabel && <div className="label" id={`${id}-label}`}>{translate(`communications.${labelText}`)}</div>}
        <PermissionGate permission={permissionId}>
          <button
            id={`${id}-preview-button`}
            type="button"
            onClick={sendEmailWithData}
            disabled={isDisabled}
          >
            {translate(`communications.${calculateButtonText()}`)}
          </button>
        </PermissionGate>
      </div>
      <style jsx>
        {`
        .control {
          display: flex;
          flex-direction: row;
          align-items: center;
          width: 100%;
          border-bottom: ${showBorder ? '1px solid var(--color-line-grey)' : 'none'};
          padding: 12px 0;
        }

        .label {
          width: 100%;
        }

        button {
          cursor: ${isDisabled ? 'auto' : 'pointer'};
          background-color: ${calculateButtonColor()};
          color: ${calculateButtonTextColor()};;
          border: 1px solid var(--color-mid-grey);
          border-radius: 21px;
          font-size: var(${fontSize});
          font-weight: var(--font-weight-body);
          height: auto;
          margin-top: 0;
          min-height: 36px;
          min-width: 123px;
          padding: 5px;
          width: auto;
          transition: background-color 0.3s linear;
        }

        button:hover {
          opacity: ${isDisabled ? '1' : '0.7'};
          transition: opacity 0.1s linear;
        }

      `}
      </style>
    </div>
  );
}

SendButton.defaultProps = {
  buttonText: 'preview',
  showLabel: true,
  showBorder: true,
  fontSize: '--font-size-body',
  telemetryEvent: null,
  telemetryValue: null,
};

SendButton.propTypes = {
  id: PropTypes.string.isRequired,
  labelText: PropTypes.string.isRequired,
  templateId: PropTypes.string.isRequired,
  requestData: PropTypes.shape({}).isRequired,
  buttonText: PropTypes.string,
  showLabel: PropTypes.bool,
  showBorder: PropTypes.bool,
  fontSize: PropTypes.string,
  telemetryEvent: PropTypes.string,
  telemetryValue: PropTypes.string,
};
